import type { TypedMutationOnQueryStarted } from '@reduxjs/toolkit/query'
import {
  hu as baseHu,
  BuildingSingleResult,
  ExportGroupEnum,
  UnitExportFieldType,
  TenantQueryModel as BaseTenantQueryModel,
  UnitQueryExtension as BaseUnitQueryExtension,
  UnitQueryModel as BaseUnitQueryModel,
} from './baseApi/hu'
import type {
  PutUnitByIdNotesApiArg,
  PutUnitByIdNotesApiResponse,
} from './baseApi/hu'
import type { baseQuery } from './baseQuery'
import type { OverrideResults } from './overrideResult'

export type * from './baseApi/hu'

export {
  BuildingQuerySortColumn,
  ExportGroupEnum,
  SortOrder,
  UnitExportFieldType,
  UnitQuerySortColumn,
} from './baseApi/hu'

export enum TenantsSortFieldEnum {
  propertyNumber = 'PropertyNumber',
  sectionNumber = 'ShareOrSectionNumber',
  tenants = 'Tenants',
  address = 'Address',
  owners = 'Owners',
}

export enum UnitListExtensionPropertiesEnum {
  ownerSince = 'OwnerSince',
  objects = 'Objects',
  meters = 'Meters',
  unitNumberDeveloper = 'UnitNumberDeveloper',
  remainingJointDebtAmount = 'RemainingJointDebtAmount',
}

export type UnitExportFieldModel = {
  id: UnitExportFieldType
  description: string
  selected: boolean
}

export type UnitExportGroupModel = {
  id: ExportGroupEnum
  description: string
  fields: UnitExportFieldModel[]
}

export type UnitExportModel = {
  groups: UnitExportGroupModel[]
  pin: string
}

// PersonUnitController.GetProxyOwnerForUnit returns a ProxyOwnerModel but is
// annotated [ProducesResponseType(typeof(IList<UnitOwnerModel>))], so the
// schema types getUnitByIdProxyowner as UnitOwnerModel[] and never includes
// this model. Fix the annotation in microservice-housingunit, regenerate, and
// this type and its OverrideResults entry below can be deleted.
export type ProxyOwnerModel = {
  name?: string | null
  email?: string | null
  phone?: string | null
  proxyOwnerSince?: string | null
}

// PersonUnitController.GetHistoricalOwners returns List<HistoricalOwnerModel>
// but is annotated [ProducesResponseType(typeof(List<MeterModel>))], so the
// schema types getUnitByIdOwnersHistorical as MeterModel[] and never includes
// this model. Fix the annotation in microservice-housingunit, regenerate, and
// this type and its OverrideResults entry below can be deleted.
export type HistoricalOwnerModel = {
  name?: string | null
  from?: string
  to?: string
}

export type ClientAddressModel = {
  address: string
  zip: string
  city: string
  addressId?: number
}

export type BuildingQueryResultModel = {
  totalCount?: number
  totalPageCount?: number
  resultCount?: number
  morePages?: boolean
  currentPage?: number
  result: BuildingSingleResult[]
}

export type UnitQueryExtensionModel = Omit<
  BaseUnitQueryExtension,
  'propertyType'
> & {
  propertyType: UnitListExtensionPropertiesEnum
}

export type UnitsQueryModel = Omit<
  BaseUnitQueryModel,
  'take' | 'skip' | 'extendedQuery'
> & {
  take: number
  skip: number
  extendedQuery?: UnitQueryExtensionModel | null
}

export type TenantsQueryModel = Omit<
  BaseTenantQueryModel,
  'take' | 'skip' | 'sortColumn'
> & {
  take: number
  skip: number
  sortColumn?: TenantsSortFieldEnum | null
}

export const onPutUnitNotesStarted: TypedMutationOnQueryStarted<
  PutUnitByIdNotesApiResponse,
  PutUnitByIdNotesApiArg,
  typeof baseQuery,
  'portal'
> = async ({ id, noteUpdateModel }, { dispatch, getState, queryFulfilled }) => {
  try {
    await queryFulfilled
  } catch {
    return
  }

  const cached = baseHu.endpoints.getUnitByIdNotes.select({ id })(getState())

  await dispatch(
    baseHu.util.upsertQueryData(
      'getUnitByIdNotes',
      { id },
      {
        ...cached.data,
        notes: noteUpdateModel.notes,
        updatedByUser: noteUpdateModel.updatedBy,
        updated: new Date().toISOString(),
      }
    )
  )
}

export const huApi = baseHu.enhanceEndpoints<
  never,
  OverrideResults<
    typeof baseHu,
    {
      getClientByClientIdQueryextensions: UnitQueryExtensionModel[]
      getClientByClientIdAddresses: ClientAddressModel[]
      getClientByClientIdExportMetadata: UnitExportModel
      getUnitByIdProxyowner: ProxyOwnerModel
      getUnitByIdOwnersHistorical: HistoricalOwnerModel[]
      postBuildingClientByClientId: BuildingQueryResultModel
    }
  >
>({
  endpoints: {
    putUnitByIdNotes: { onQueryStarted: onPutUnitNotesStarted },
  },
})

export const {
  useGetBuildingByBuildingIdQuery,
  useGetBuildingClientByClientIdBuildingtypesQuery,
  useGetBuildingForClientByClientIdQuery,
  useGetBuildingByBuildingIdUnitsQuery,
  usePostBuildingClientByClientIdQuery,
  usePostClientByClientIdExportExcelMutation,
  useGetClientByClientIdQueryextensionsQuery,
  useGetClientByClientIdAddressesQuery,
  useGetClientByClientIdExportMetadataQuery,
  usePostClientByClientIdTenantsQuery,
  usePostClientByClientIdUnitsQuery,
  usePostClientByClientIdUsersMutation,
  useGetNavneregisterThirdpartyinformationByIdDownloadQuery,
  useGetNavneregisterInvoicesByInvoiceIdFileQuery,
  useGetNavneregisterInvoicesQuery,
  useGetNavneregisterUnitsQuery,
  useGetNavneregisterThirdpartyinformationQuery,
  useGetUnitRelationtypesQuery,
  useGetUnitByIdAdditionalinfoQuery,
  usePostUnitByIdCoownersMutation,
  useGetUnitByIdIsaffiliatedQuery,
  useGetUnitByIdHascoownerprocessQuery,
  usePostUnitByIdResidentsMutation,
  useGetUnitByIdResidentsQuery,
  useDeleteUnitByIdResidentsAndCoResidencyIdMutation,
  usePutUnitByIdResidentsAndCoResidencyIdMutation,
  useGetUnitByIdInvoicesAndInvoiceIdFileQuery,
  useGetUnitByIdOwnersQuery,
  useGetUnitByIdQuery,
  useGetUnitByIdAnnualcycleQuery,
  useGetUnitByIdMetersQuery,
  useGetUnitByIdNotesQuery,
  usePutUnitByIdNotesMutation,
  useGetUnitByIdObjectsQuery,
  useGetUnitByIdProxyownerQuery,
  useGetUnitByIdInvoicesQuery,
  useGetUnitByIdJointcostQuery,
  useGetUnitByIdJointcostpersonalQuery,
  useGetUnitChangecoownerparametersQuery,
  useGetUnitByIdOwnersHistoricalQuery,
  useGetUnitIsssnvalidBySsnQuery,
  useGetSiteSiteBySiteIdQuery,
  usePostSiteClientByClientIdMutation,
  useGetUnitByUnitIdTenantsQuery,
  usePutUnitByUnitIdTenantsMutation,
  useGetUnitByUnitIdTenantsHistoricalQuery,
} = huApi
